import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';
import { LinkContainer } from 'react-router-bootstrap';
import Dropdown from 'react-bootstrap/lib/Dropdown';
import MenuItem from 'react-bootstrap/lib/MenuItem';
import { ChevronDown, Mail, MailCheck } from 'lucide-react';
import browseUserCardStyles from 'components/Browse/UserCard/BrowseUserCard.module.scss';
import chooserStyles from './InviteProjectChooser.module.scss';
import localStyles from './InviteToProjectButton.module.scss';

const INVITE_LABEL = 'Invite';
const INVITED_LABEL = 'Invited';
const INVITE_ROW_ICON_SIZE = 18;
const ALREADY_INVITED_NOTE = 'Already invited';

function validCollaborations(collaborations) {
  const list = Array.isArray(collaborations) ? collaborations : [];
  return list.filter((b) => b && typeof b === 'object' && b.id != null);
}

function stopEvent(e) {
  e.stopPropagation();
}

/**
 * Browse hit invite control (TalentCard-style Dropdown when multiple jobs).
 * Mounted into InstantSearch hits via `mountBrowseInviteIslands` so Dropdown state works.
 */
const InviteToProjectButton = ({
  user,
  collaborations,
  isLoadingOwnBriefs,
  isBriefInviteBlockedForUser,
  isMobile,
  authUser,
  onSendInvite,
  onOpenChooserModal,
}) => {
  if (!user) {
    return null;
  }

  const trimmedUser =
    user.username != null && String(user.username).trim() !== ''
      ? String(user.username).trim()
      : '';
  if (!trimmedUser) {
    return null;
  }

  const uid =
    user.id != null && String(user.id).trim() !== ''
      ? String(user.id).trim()
      : '';

  const briefs = validCollaborations(collaborations);
  const hasBriefs = briefs.length > 0;

  const requireAuthClick = (e) => {
    stopEvent(e);
    if (!authUser) {
      e.preventDefault();
      window.location.href = '/signin';
    }
  };

  const hireLink = (
    <Link
      to={`/${trimmedUser}/hire`}
      className={localStyles.inviteControl}
      onClick={requireAuthClick}
    >
      {INVITE_LABEL}
    </Link>
  );

  if (isLoadingOwnBriefs) {
    return (
      <button
        type="button"
        className={localStyles.inviteControl}
        disabled
        onClick={stopEvent}
      >
        {INVITE_LABEL}
      </button>
    );
  }

  if (!hasBriefs || !uid) {
    return <span onClick={stopEvent}>{hireLink}</span>;
  }

  if (briefs.length === 1) {
    const only = briefs[0];
    const blocked =
      typeof isBriefInviteBlockedForUser === 'function'
        ? isBriefInviteBlockedForUser(only, uid)
        : false;
    return (
      <button
        type="button"
        className={
          blocked
            ? `${localStyles.inviteControl} ${localStyles.inviteControlAlreadyInvited}`
            : localStyles.inviteControl
        }
        disabled={blocked}
        onClick={(e) => {
          stopEvent(e);
          if (!authUser) {
            window.location.href = '/signin';
            return;
          }
          if (!blocked) {
            onSendInvite(only, uid);
          }
        }}
        aria-label={blocked ? 'Already invited to this job' : undefined}
      >
        {blocked ? INVITED_LABEL : INVITE_LABEL}
      </button>
    );
  }

  if (isMobile) {
    return (
      <button
        type="button"
        className={localStyles.inviteControl}
        onClick={(e) => {
          stopEvent(e);
          if (!authUser) {
            window.location.href = '/signin';
            return;
          }
          onOpenChooserModal({ username: trimmedUser, userId: uid });
        }}
      >
        {INVITE_LABEL}
      </button>
    );
  }

  const briefDisabled = (brief) =>
    typeof isBriefInviteBlockedForUser === 'function'
      ? isBriefInviteBlockedForUser(brief, uid)
      : false;

  return (
    <Dropdown
      id={`browse-invite-dd-${uid}`}
      pullRight
      onClick={stopEvent}
      className={localStyles.inviteDropdown}
    >
      <Dropdown.Toggle
        noCaret
        className={localStyles.inviteControl}
        onClick={(e) => {
          stopEvent(e);
          if (!authUser) {
            e.preventDefault();
            window.location.href = '/signin';
          }
        }}
      >
        {INVITE_LABEL}
        <ChevronDown
          width={16}
          height={16}
          className={localStyles.chevronIcon}
          aria-hidden
        />
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <LinkContainer to={`/${trimmedUser}/hire`} onClick={requireAuthClick}>
          <MenuItem eventKey="new">Contact for new project</MenuItem>
        </LinkContainer>
        <MenuItem divider />
        <MenuItem header eventKey="header">
          Request application to:
        </MenuItem>
        {briefs.map((brief, key) => {
          const disabled = briefDisabled(brief);
          return (
            <MenuItem
              key={brief.id != null ? brief.id : key}
              eventKey={String(brief.id != null ? brief.id : key)}
              disabled={disabled}
              className={
                disabled
                  ? chooserStyles.inviteChooserMenuItemInvited
                  : undefined
              }
            >
              <div
                className={
                  disabled
                    ? `${browseUserCardStyles.collaborationDropdownItem} ${chooserStyles.inviteChooserAlreadyInvited}`
                    : browseUserCardStyles.collaborationDropdownItem
                }
                onClick={
                  disabled
                    ? undefined
                    : (e) => {
                        stopEvent(e);
                        onSendInvite(brief, uid);
                      }
                }
                role="presentation"
              >
                {disabled ? (
                  <span className={chooserStyles.inviteChooserInvitedBlock}>
                    <MailCheck
                      className={chooserStyles.inviteChooserIconSent}
                      size={INVITE_ROW_ICON_SIZE}
                      aria-hidden
                    />
                    <span className={chooserStyles.inviteChooserInvitedBody}>
                      <span className={chooserStyles.inviteChooserJobText}>
                        {brief.text}
                      </span>
                      <span
                        className={
                          chooserStyles.inviteChooserAlreadyInvitedNote
                        }
                      >
                        {ALREADY_INVITED_NOTE}
                      </span>
                    </span>
                  </span>
                ) : (
                  <span className={chooserStyles.inviteChooserJobRow}>
                    <Mail
                      className={chooserStyles.inviteChooserIconAvailable}
                      size={INVITE_ROW_ICON_SIZE}
                      aria-hidden
                    />
                    <span className={chooserStyles.inviteChooserJobText}>
                      {brief.text}
                    </span>
                  </span>
                )}
              </div>
            </MenuItem>
          );
        })}
      </Dropdown.Menu>
    </Dropdown>
  );
};

InviteToProjectButton.propTypes = {
  user: PropTypes.shape({
    username: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  collaborations: PropTypes.array,
  isLoadingOwnBriefs: PropTypes.bool,
  isBriefInviteBlockedForUser: PropTypes.func,
  isMobile: PropTypes.bool,
  authUser: PropTypes.object,
  onSendInvite: PropTypes.func.isRequired,
  onOpenChooserModal: PropTypes.func.isRequired,
};

InviteToProjectButton.defaultProps = {
  user: null,
  collaborations: [],
  isLoadingOwnBriefs: false,
  isBriefInviteBlockedForUser: null,
  isMobile: false,
  authUser: null,
};

export default InviteToProjectButton;
